import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {
    App,
    Button,
    Card,
    Col,
    Empty,
    Input,
    Modal,
    Pagination,
    Row,
    Select,
    Space,
    Spin,
    Tag,
    Tooltip,
    Typography
} from 'antd';
import {
    ClockCircleOutlined,
    FallOutlined,
    LoadingOutlined,
    MinusOutlined,
    ReloadOutlined,
    RiseOutlined,
    StarFilled,
    ThunderboltOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {News, NewsAnalysis, NewsFilter, newsService, SentimentStats} from '../../services/newsService';

const {Title, Text, Paragraph} = Typography;
const {Option} = Select;

const NewsPage: React.FC = () => {
    const {message: messageApi} = App.useApp();
    const navigate = useNavigate();
    const [newsList, setNewsList] = useState<News[]>([]);
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState<SentimentStats | null>(null);
    const [filter, setFilter] = useState<NewsFilter>({page: 0, size: 12});
    const [pagination, setPagination] = useState({current: 0, total: 0, pageSize: 12});
    const [timeRange, setTimeRange] = useState<string>('7day');

    // 分析弹窗状态
    const [analysisModalVisible, setAnalysisModalVisible] = useState(false);
    const [currentNews, setCurrentNews] = useState<News | null>(null);
    const [analysisLoading, setAnalysisLoading] = useState(false);
    const [analysis, setAnalysis] = useState<NewsAnalysis | null>(null);

    const loadNews = async () => {
        try {
            const data = await newsService.getNewsList(filter);
            setNewsList(data.content || []);
            setPagination({
                current: data.number,
                total: data.totalElements,
                pageSize: data.size
            });
        } catch (err) {
            // 静默失败，不提示不渲染
        }
    };

    const loadStats = async (timeRange: string) => {
        try {
            const data = await newsService.getSentimentStats(timeRange);
            setStats(data);
        } catch (err) {
            // 忽略错误
        }
    };

    // 初始加载和定时静默刷新
    useEffect(() => {
        loadNews();
        loadStats(timeRange);
        const interval = setInterval(() => {
            loadNews();
            loadStats(timeRange);
        }, 60000);
        return () => clearInterval(interval);
    }, [filter]);

    const handlePageChange = (page: number, pageSize: number) => {
        setFilter(prev => ({...prev, page: page - 1, size: pageSize}));
    };

    const handleAnalyze = async (news: News, e: React.MouseEvent) => {
        e.stopPropagation();
        setCurrentNews(news);
        setAnalysisModalVisible(true);
        setAnalysisLoading(true);
        setAnalysis(null);

        try {
            const existing = await newsService.getAnalysis(news.id);
            if (existing) {
                setAnalysis(existing);
                setAnalysisLoading(false);
                return;
            }
        } catch (err) {
            // 如果是"暂无分析结果"，继续触发分析；否则显示错误
            if (!(err instanceof Error && err.message.includes('暂无'))) {
                messageApi.error('获取分析结果失败');
                setAnalysisLoading(false);
                return;
            }
        }

        // 触发新分析
        try {
            const result = await newsService.analyzeNews(news.id);
            setAnalysis(result);
        } catch (err) {
            messageApi.error('分析失败，请稍后重试');
        } finally {
            setAnalysisLoading(false);
        }
    };

    const getSentimentConfig = (sentiment?: string) => {
        switch (sentiment) {
            case 'POSITIVE':
                return {label: '利好', color: 'var(--pt-bull)', bg: 'var(--pt-bull-dim)', icon: <RiseOutlined/>};
            case 'NEGATIVE':
                return {label: '利空', color: 'var(--pt-bear)', bg: 'var(--pt-bear-dim)', icon: <FallOutlined/>};
            case 'NEUTRAL':
                return {label: '中性', color: 'var(--pt-blue)', bg: 'var(--pt-blue-dim)', icon: <MinusOutlined/>};
            default:
                return {
                    label: '未分析',
                    color: 'var(--pt-text-secondary)',
                    bg: 'var(--pt-bg-card)',
                    icon: <MinusOutlined/>
                };
        }
    };

    const parseJsonArray = (jsonStr?: string): string[] => {
        if (!jsonStr) return [];
        try {
            return JSON.parse(jsonStr);
        } catch {
            return [];
        }
    };

    // 计算统计百分比
    const getStatPercent = (value: number) => {
        if (!stats) return 0;
        const total = stats.positive + stats.negative + stats.neutral;
        return total > 0 ? Math.round((value / total) * 100) : 0;
    };

    return (
        <div style={{padding: 16}}>
            {/* 筛选栏 - 紧凑单行 */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                marginBottom: 16,
                padding: '12px 16px',
                background: 'rgb(12, 17, 32)',
                borderRadius: 8
            }}>
                <Select
                    style={{width: 120}}
                    placeholder="分类"
                    allowClear
                    value={filter.category}
                    onChange={val => setFilter(prev => ({...prev, category: val, page: 0}))}
                    options={[
                        {value: 'CRYPTO', label: '加密货币'},
                        {value: 'FOREX', label: '外汇'},
                        {value: 'MACRO', label: '宏观'},
                        {value: 'STOCK', label: '股票'}
                    ]}
                />
                <Select
                    style={{width: 100}}
                    placeholder="情绪"
                    allowClear
                    value={filter.sentiment}
                    onChange={val => setFilter(prev => ({...prev, sentiment: val, page: 0}))}
                    options={[
                        {value: 'POSITIVE', label: '利好'},
                        {value: 'NEGATIVE', label: '利空'},
                        {value: 'NEUTRAL', label: '中性'}
                    ]}
                />
                <Select
                    style={{width: 100}}
                    value={timeRange}
                    onChange={val => {
                        setTimeRange(val);
                        setFilter(prev => ({...prev, timeRange: val, page: 0}));
                    }}
                    options={[
                        {value: '7day', label: '近7天'},
                        {value: '3day', label: '近3天'},
                        {value: 'month', label: '本月'},
                        {value: 'week', label: '本周'},
                        {value: 'yesterday', label: '昨天'},
                        {value: 'today', label: '今天'}
                    ]}
                />
                <Input.Search
                    style={{width: 200, marginLeft: 'auto'}}
                    placeholder="搜索关键词"
                    allowClear
                    onSearch={val => setFilter(prev => ({...prev, keyword: val || undefined, page: 0}))}
                />
                <Tooltip title="刷新数据">
                    <Button icon={<ReloadOutlined/>} onClick={() => {
                        loadNews();
                        loadStats(timeRange);
                    }}/>
                </Tooltip>
            </div>

            {/* 统计卡片 - 紧凑一行 */}
            {stats && (
                <div style={{
                    display: 'flex',
                    gap: 12,
                    marginBottom: 16
                }}>
                    <div style={{
                        flex: 1,
                        padding: '10px 16px',
                        background: 'rgba(0, 217, 126, 0.08)',
                        border: '1px solid rgba(0, 217, 126, 0.15)',
                        borderRadius: 8,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12
                    }}>
                        <div style={{display: 'flex', flexDirection: 'column', gap: 2}}>
                            <div style={{fontSize: 11, color: 'var(--pt-text-secondary)'}}>
                                <RiseOutlined style={{color: 'var(--pt-bull)', fontSize: 12, marginRight: 4}}/>利好
                            </div>
                            <div style={{fontSize: 12, color: 'var(--pt-text-secondary)'}}>{stats.positive} 条</div>
                        </div>
                        <div style={{flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                            <div style={{fontSize: 24, fontWeight: 700, color: 'var(--pt-bull)', lineHeight: 1}}>
                                {getStatPercent(stats.positive)}%
                            </div>
                        </div>
                    </div>
                    <div style={{
                        flex: 1,
                        padding: '10px 16px',
                        background: 'rgba(255, 82, 82, 0.08)',
                        border: '1px solid rgba(255, 82, 82, 0.15)',
                        borderRadius: 8,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12
                    }}>
                        <div style={{display: 'flex', flexDirection: 'column', gap: 2}}>
                            <div style={{fontSize: 11, color: 'var(--pt-text-secondary)'}}>
                                <FallOutlined style={{color: 'var(--pt-bear)', fontSize: 12, marginRight: 4}}/>利空
                            </div>
                            <div style={{fontSize: 12, color: 'var(--pt-text-secondary)'}}>{stats.negative} 条</div>
                        </div>
                        <div style={{flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                            <div style={{fontSize: 24, fontWeight: 700, color: 'var(--pt-bear)', lineHeight: 1}}>
                                {getStatPercent(stats.negative)}%
                            </div>
                        </div>
                    </div>
                    <div style={{
                        flex: 1,
                        padding: '10px 16px',
                        background: 'rgba(255, 255, 255, 0.03)',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        borderRadius: 8,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12
                    }}>
                        <div style={{display: 'flex', flexDirection: 'column', gap: 2}}>
                            <div style={{fontSize: 11, color: 'var(--pt-text-secondary)'}}>
                                <MinusOutlined
                                    style={{color: 'var(--pt-text-secondary)', fontSize: 12, marginRight: 4}}/>中性
                            </div>
                            <div style={{fontSize: 12, color: 'var(--pt-text-secondary)'}}>{stats.neutral} 条</div>
                        </div>
                        <div style={{flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                            <div
                                style={{fontSize: 24, fontWeight: 700, color: 'var(--pt-text-primary)', lineHeight: 1}}>
                                {getStatPercent(stats.neutral)}%
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* 新闻列表 */}
            <Spin spinning={loading} indicator={<LoadingOutlined spin/>}>
                {newsList.length === 0 ? (
                    <Empty description="暂无新闻数据" style={{padding: '60px 0'}}/>
                ) : (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                        gridAutoFlow: 'dense',
                        gridAutoRows: '200px',
                        gap: 16,
                    }}>
                        {newsList.map(news => {
                            const sentimentConfig = getSentimentConfig(news.sentiment);
                            // 优先显示翻译内容
                            const displayTitle = news.hasTranslation && news.titleTranslated ? news.titleTranslated : news.title;
                            const displaySummary = news.hasTranslation && news.summaryTranslated ? news.summaryTranslated : news.summary;
                            // 重要新闻占据2列2行
                            const isImportant = news.importance === 2;
                            return (
                                <Card
                                    key={news.id}
                                    hoverable
                                    style={{
                                        borderRadius: 8,
                                        transition: 'all 0.2s',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gridColumn: isImportant ? 'span 2' : undefined,
                                        gridRow: isImportant ? 'span 2' : undefined,
                                    }}
                                    styles={{body: {padding: 16, flex: 1, display: 'flex', flexDirection: 'column'}}}
                                    onClick={() => window.open(news.originalUrl, '_blank')}
                                >
                                    {/* 来源和时间 */}
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        marginBottom: 8,
                                        fontSize: 12,
                                        color: '#8c8c8c'
                                    }}>
                    <span>
                      {news.sourceName || '未知来源'}
                        {news.hasTranslation && (
                            <Tag style={{
                                marginLeft: 4,
                                fontSize: 9,
                                transform: 'scale(0.5)',
                                transformOrigin: 'left center'
                            }} color="blue">翻译</Tag>
                        )}
                        {isImportant && (
                            <Tag style={{marginLeft: 4, fontSize: 10}} color="gold" icon={<StarFilled/>}>重要</Tag>
                        )}
                    </span>
                                        <span style={{display: 'flex', alignItems: 'center', gap: 4}}>
                      <ClockCircleOutlined/>
                                            {dayjs(news.publishedAt).format('MM-DD HH:mm')}
                    </span>
                                    </div>

                                    {/* 标题 - 重要新闻显示更多行 */}
                                    <Title
                                        level={5}
                                        ellipsis={{rows: isImportant ? 3 : 2}}
                                        style={{
                                            marginBottom: 8,
                                            fontSize: isImportant ? 16 : 14,
                                            minHeight: isImportant ? 72 : 44
                                        }}
                                    >
                                        {displayTitle}
                                    </Title>

                                    {/* 摘要 - 重要新闻显示更多行 */}
                                    {displaySummary && (
                                        <Paragraph
                                            ellipsis={{rows: isImportant ? 6 : 3}}
                                            style={{
                                                fontSize: isImportant ? 13 : 12,
                                                color: '#8c8c8c',
                                                marginBottom: 8,
                                                minHeight: isImportant ? 130 : 54
                                            }}
                                        >
                                            {displaySummary}
                                        </Paragraph>
                                    )}

                                    {/* 底部标签 - 固定在底部 */}
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        paddingTop: 8,
                                        borderTop: '1px solid #f0f0f0',
                                        marginTop: 'auto'
                                    }}>
                                        <div style={{display: 'flex', gap: 4, flexWrap: 'wrap' as const}}>
                                            <Tag
                                                style={{
                                                    margin: 0,
                                                    background: sentimentConfig.bg,
                                                    color: sentimentConfig.color,
                                                    border: 'none',
                                                    fontSize: 11
                                                }}
                                            >
                                                {sentimentConfig.label}
                                            </Tag>
                                            {parseJsonArray(news.marketTags).slice(0, isImportant ? 4 : 2).map(tag => (
                                                <Tag
                                                    key={tag}
                                                    style={{margin: 0, fontSize: 11}}
                                                >
                                                    {tag}
                                                </Tag>
                                            ))}
                                        </div>
                                        <Tooltip title="AI分析交易机会">
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<ThunderboltOutlined style={{color: 'var(--pt-purple)'}}/>}
                                                onClick={(e) => handleAnalyze(news, e)}
                                                style={{padding: '0 4px'}}
                                            />
                                        </Tooltip>
                                    </div>
                                </Card>
                            );
                        })}
                    </div>
                )}
            </Spin>

            {/* 分页器 */}
            {pagination.total > 0 && (
                <div style={{display: 'flex', justifyContent: 'center', marginTop: 24}}>
                    <Pagination
                        current={pagination.current + 1}
                        total={pagination.total}
                        pageSize={pagination.pageSize}
                        showSizeChanger={false}
                        showTotal={total => `共 ${total} 条`}
                        onChange={handlePageChange}
                    />
                </div>
            )}

            {/* 分析弹窗 */}
            <AnalysisModal
                visible={analysisModalVisible}
                news={currentNews}
                analysis={analysis}
                loading={analysisLoading}
                onClose={() => setAnalysisModalVisible(false)}
            />
        </div>
    );
};

// 分析结果弹窗组件
const AnalysisModal: React.FC<{
    visible: boolean;
    news: News | null;
    analysis: NewsAnalysis | null;
    loading: boolean;
    onClose: () => void;
}> = ({visible, news, analysis, loading, onClose}) => {
    const navigate = useNavigate();
    if (!news) return null;

    const parseJsonArray = (jsonStr?: string): string[] => {
        if (!jsonStr) return [];
        try {
            return JSON.parse(jsonStr);
        } catch {
            return [];
        }
    };

    const getOpportunityConfig = (opportunity?: string) => {
        switch (opportunity) {
            case 'LONG':
                return {
                    label: '做多机会',
                    color: 'var(--pt-bull)',
                    icon: <RiseOutlined/>,
                    bgColor: 'var(--pt-bull-dim)'
                };
            case 'SHORT':
                return {label: '做空机会', color: 'var(--pt-bear)', icon: <FallOutlined/>, bgColor: 'rgb(120, 73, 73)'};
            case 'NEUTRAL':
                return {label: '中性', color: '#8c8c8c', icon: <MinusOutlined/>, bgColor: 'var(--pt-blue-dim)'};
            case 'WAIT':
                return {
                    label: '建议观望',
                    color: 'var(--pt-amber)',
                    icon: <MinusOutlined/>,
                    bgColor: 'rgb(64, 80, 96)'
                };
            default:
                return {label: '未知', color: '#8c8c8c', icon: <MinusOutlined/>, bgColor: 'var(--pt-blue-dim)'};
        }
    };

    const config = getOpportunityConfig(analysis?.tradingOpportunity);

    return (
        <Modal
            title={
                <Space>
                    <ThunderboltOutlined style={{color: 'var(--pt-purple)'}}/>
                    <span>AI 深度分析</span>
                </Space>
            }
            open={visible}
            onCancel={onClose}
            width={640}
            footer={[
                <Button key="close" onClick={onClose}>关闭</Button>,
                analysis?.affectedSymbols && (
                    <Button key="trade" type="primary" onClick={() => {
                        const symbols = parseJsonArray(analysis.affectedSymbols);
                        if (symbols[0]) {
                            navigate(`/trading?symbol=${symbols[0]}`);
                        } else {
                            navigate('/trading');
                        }
                    }}>
                        前往交易
                    </Button>
                )
            ]}
        >
            {loading ? (
                <div style={{textAlign: 'center', padding: '48px 0'}}>
                    <Spin size="large" indicator={<LoadingOutlined spin/>}/>
                    <Text type="secondary" style={{display: 'block', marginTop: 16}}>正在分析中...</Text>
                </div>
            ) : analysis ? (
                <div>
                    {/* 新闻标题 */}
                    <div style={{
                        padding: 12,
                        borderRadius: 4,
                        marginBottom: 16
                    }}>
                        <Text type="secondary" style={{fontSize: 12}}>分析新闻</Text>
                        <Title level={5} style={{margin: '4px 0 0'}}>{news.title}</Title>
                    </div>

                    {/* 核心结论 */}
                    <Row gutter={12} style={{marginBottom: 16}}>
                        <Col span={12}>
                            <div style={{padding: 12, background: config.bgColor, borderRadius: 4}}>
                                <Text type="secondary" style={{fontSize: 12, color: '#fff'}}>交易方向</Text>
                                <div style={{marginTop: 4, display: 'flex', alignItems: 'center', gap: 8}}>
                                    <span style={{fontSize: 18, color: config.color}}>{config.icon}</span>
                                    <Text strong style={{fontSize: 16, color: '#fff'}}>{config.label}</Text>
                                </div>
                            </div>
                        </Col>
                        <Col span={12}>
                            <div style={{padding: 12, background: 'rgb(88, 159, 162)', borderRadius: 4}}>
                                <Text type="secondary" style={{fontSize: 12, color: '#fff'}}>机会评分</Text>
                                <div style={{marginTop: 4}}>
                                    <Text strong style={{fontSize: 16, color: '#fff'}}>
                                        {((analysis.opportunityScore ?? 0) * 100).toFixed(0)}%
                                    </Text>
                                    <Text style={{marginLeft: 8, fontSize: 12, color: 'rgba(255,255,255,0.8)'}}>
                                        置信度 {((analysis.confidence ?? 0) * 100).toFixed(0)}%
                                    </Text>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* 影响品种 */}
                    {analysis.affectedSymbols && (
                        <div style={{marginBottom: 12}}>
                            <Text type="secondary" style={{fontSize: 12}}>影响品种</Text>
                            <div style={{display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4}}>
                                {parseJsonArray(analysis.affectedSymbols).map(s => (
                                    <Tag key={s} color="blue">{s}</Tag>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* 分析摘要 */}
                    {analysis.analysisSummary && (
                        <div style={{marginBottom: 12}}>
                            <Text type="secondary" style={{fontSize: 12}}>分析摘要</Text>
                            <Paragraph style={{marginTop: 4, marginBottom: 0}}>{analysis.analysisSummary}</Paragraph>
                        </div>
                    )}

                    {/* 操作建议 */}
                    {analysis.actionSuggestion && (
                        <div style={{padding: 12, background: 'var(--pt-bull-dim)', borderRadius: 4, marginBottom: 8}}>
                            <Text style={{color: '#fff', fontWeight: 500}}>💡 操作建议</Text>
                            <Paragraph style={{
                                marginTop: 4,
                                marginBottom: 0,
                                color: '#fff'
                            }}>{analysis.actionSuggestion}</Paragraph>
                        </div>
                    )}

                    {/* 风险提示 */}
                    {analysis.riskWarning && (
                        <div style={{padding: 12, background: 'rgb(36, 80, 114)', borderRadius: 4, marginBottom: 8}}>
                            <Text style={{color: '#fff', fontWeight: 500}}>⚠️ 风险提示</Text>
                            <Paragraph style={{
                                marginTop: 4,
                                marginBottom: 0,
                                color: '#fff'
                            }}>{analysis.riskWarning}</Paragraph>
                        </div>
                    )}

                    {/* 关键因素 */}
                    {analysis.keyFactors && (
                        <div style={{marginBottom: 12}}>
                            <Text type="secondary" style={{fontSize: 12}}>关键因素</Text>
                            <ul style={{marginTop: 4, marginBottom: 0, paddingLeft: 16}}>
                                {parseJsonArray(analysis.keyFactors).map((factor, i) => (
                                    <li key={i} style={{color: '#595959'}}>{factor}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* 元信息 */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: 11,
                        color: '#bfbfbf',
                        marginTop: 12,
                        paddingTop: 8,
                        borderTop: '1px solid #f0f0f0'
                    }}>
                        <span>模型: {analysis.modelUsed || 'unknown'}</span>
                        <span>{dayjs(analysis.createdAt).format('YYYY-MM-DD HH:mm:ss')}</span>
                    </div>
                </div>
            ) : (
                <Empty description="暂无分析结果"/>
            )}
        </Modal>
    );
};

export default NewsPage;